import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IdAustria from '@/pages/Auth/IdAustria';
import Spinner from '@/components/Spinner';
import styles from './SafeTransferAccess.module.scss';

export const ID_AUSTRIA_STASH_KEY = 'safeTransferIdAustria';
// The login flow sends no `state`; safe-transfer verification sends
// `st.<transferId>.<uuid>` — that prefix is what tells the two flows apart
// on the shared registered callback, independent of any stored state.
export const ID_AUSTRIA_STATE_PREFIX = 'st.';

export type IdAustriaStash = {
  state: string;
  returnTo: string;
};

const readStash = (): IdAustriaStash | null => {
  try {
    const raw = sessionStorage.getItem(ID_AUSTRIA_STASH_KEY);
    return raw ? (JSON.parse(raw) as IdAustriaStash) : null;
  } catch {
    return null;
  }
};

const transferIdFromState = (state: string): string | null => {
  const [, transferId] = state.split('.');
  return transferId || null;
};

// The ID Austria client only allows registered redirect URIs, so the
// safe-transfer verification borrows the login flow's registered callback.
// This gate decides whose callback it is by the `state` param; the regular
// login callback renders untouched when it isn't ours.
const IdAustriaReturnGate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Pure read — the single-use clear happens in the effect below, so React's
  // double-invoked initializers (StrictMode) can't lose the stash.
  const [stash] = useState<IdAustriaStash | null>(readStash);

  const returnedState = searchParams.get('state');
  const isSafeTransferReturn = !!returnedState?.startsWith(
    ID_AUSTRIA_STATE_PREFIX
  );

  useEffect(() => {
    if (!isSafeTransferReturn || !returnedState) {
      return;
    }
    sessionStorage.removeItem(ID_AUSTRIA_STASH_KEY);

    const code = searchParams.get('code');
    const transferId = transferIdFromState(returnedState);
    const returnTo = transferId
      ? `/safe-data-transfer/${transferId}/access`
      : '/';

    // Classic OAuth CSRF / code-injection guard: without a matching stashed
    // state the code is never exchanged — back to the gate with an error.
    if (!code || !stash || stash.state !== returnedState) {
      void navigate(`${returnTo}?verror=1`, { replace: true });
      return;
    }
    void navigate(`${stash.returnTo}?code=${encodeURIComponent(code)}`, {
      replace: true,
    });
  }, [isSafeTransferReturn, navigate, returnedState, searchParams, stash]);

  if (!isSafeTransferReturn) {
    return <IdAustria />;
  }

  return (
    <div className={styles.loading}>
      <Spinner />
    </div>
  );
};

export default IdAustriaReturnGate;
